import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, CreditCard, Loader2, Search, User, XCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import CrmColumnHeading from "@/components/CrmColumnHeading";
import CrmFilterTiles from "@/components/CrmFilterTiles";
import CrmTablePanel from "@/components/CrmTablePanel";

/**
 * Filter buckets for the Stripe membership table.
 *
 * Stripe's own subscription statuses, grouped the way an operator reads them, plus
 * `comped` — which is not a status at all but a membership where nothing was
 * collected. Those are otherwise indistinguishable from paid ones at a glance,
 * which is how they went unnoticed, so they get their own filter.
 */
type MembershipFilter = "all" | "active" | "past_due" | "cancelled" | "comped";

const membershipFilterMeta = {
  all: { label: "Memberships", color: "#F9A8D4", background: "rgba(233,30,140,.10)", border: "rgba(233,30,140,.22)", icon: CreditCard },
  active: { label: "Active", color: "#86EFAC", background: "rgba(34,197,94,.12)", border: "rgba(34,197,94,.28)", icon: CheckCircle2 },
  past_due: { label: "Past due", color: "#FCD34D", background: "rgba(245,158,11,.12)", border: "rgba(245,158,11,.28)", icon: Clock3 },
  cancelled: { label: "Cancelled", color: "#FDA4AF", background: "rgba(244,63,94,.12)", border: "rgba(244,63,94,.28)", icon: XCircle },
  comped: { label: "Comped", color: "#C4B5FD", background: "rgba(139,92,246,.12)", border: "rgba(139,92,246,.28)", icon: CreditCard },
} as const satisfies Record<MembershipFilter, { label: string; color: string; background: string; border: string; icon: typeof CreditCard }>;

type MembershipRow = {
  status: string;
  comped: boolean;
  cancelAtPeriodEnd: boolean;
  name: string | null;
  email: string | null;
  discountCode: string | null;
  subscriptionId: string;
  paymentIntentId: string | null;
  startedAt: string | null;
  renewsAt: string | null;
  amountPaidCents: number | null;
  currency: string | null;
  discountLabel: string | null;
};

/**
 * Active and Comped are independent assignments, not competing Stripe states.
 * A fully discounted but active subscription belongs to both operating views,
 * while a cancelled subscription can never be counted as actively serving a member.
 */
function isActiveMembership(row: MembershipRow) {
  return row.status === "active" || row.status === "trialing";
}

function isCancelledMembership(row: MembershipRow) {
  return row.status === "canceled" || row.status === "incomplete_expired";
}

function isPastDueMembership(row: MembershipRow) {
  return row.status === "past_due" || row.status === "unpaid" || row.status === "incomplete";
}

function matchesMembershipFilter(row: MembershipRow, filter: MembershipFilter) {
  if (filter === "all") return true;
  if (filter === "active") return isActiveMembership(row);
  if (filter === "comped") return row.comped;
  if (filter === "past_due") return isPastDueMembership(row);
  return isCancelledMembership(row);
}

const membershipCell: React.CSSProperties = {
  padding: ".6rem",
  borderBottom: "1px solid rgba(255,255,255,.06)",
  color: "#CBD5E1",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
  verticalAlign: "top",
};

/** Cents to a readable amount. Zero renders as an amount, not as blank. */
function formatMoney(cents: number, currency: string | null) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? "USD",
  }).format(cents / 100);
}

function formatStripeDate(iso: string | null) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));
}

export default function PaymentRecordsView() {
  const [membershipFilter, setMembershipFilter] = useState<MembershipFilter>("all");
  const [membershipSearch, setMembershipSearch] = useState("");
  const [failedPaymentsSearch, setFailedPaymentsSearch] = useState("");
  /*
   * Membership subscriptions, read live from Stripe and scoped to the configured
   * membership price. There is no local table holding what was collected — only
   * that a checkout started — so Stripe is read directly rather than mirrored.
   */
  const membershipQuery = trpc.members.adminStripeTransactions.useQuery(undefined, { retry: false });
  const failedPaymentsQuery = trpc.members.adminFailedStripePayments.useQuery(undefined, { retry: false });

  const membershipRows = membershipQuery.data?.rows ?? [];

  const membershipSummary = useMemo(() => {
    return {
      all: membershipRows.length,
      active: membershipRows.filter(isActiveMembership).length,
      past_due: membershipRows.filter(isPastDueMembership).length,
      cancelled: membershipRows.filter(isCancelledMembership).length,
      comped: membershipRows.filter(row => row.comped).length,
    } satisfies Record<MembershipFilter, number>;
  }, [membershipRows]);

  const visibleMemberships = useMemo(() => {
    const needle = membershipSearch.trim().toLowerCase();
    /*
     * Sorted by member, A–Z. These arrive in Stripe's own order, which is by
     * subscription creation and looks arbitrary to anyone scanning for a name.
     * Rows without a name sort last rather than clustering under an empty string
     * at the top.
     */
    const byName = (left: typeof membershipRows[number], right: typeof membershipRows[number]) => {
      const leftName = left.name?.trim() || left.email?.trim() || "";
      const rightName = right.name?.trim() || right.email?.trim() || "";
      if (!leftName) return rightName ? 1 : 0;
      if (!rightName) return -1;
      return leftName.localeCompare(rightName);
    };
    return [...membershipRows].sort(byName).filter(row => {
      if (!matchesMembershipFilter(row, membershipFilter)) return false;
      if (!needle) return true;
      return [row.name, row.email, row.discountCode, row.subscriptionId, row.paymentIntentId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [membershipFilter, membershipRows, membershipSearch]);

  const visibleFailedPayments = useMemo(() => {
    const needle = failedPaymentsSearch.trim().toLowerCase();
    const rows = failedPaymentsQuery.data?.rows ?? [];
    if (!needle) return rows;
    return rows.filter(row =>
      [row.name, row.email, row.failureCode, row.failureMessage, row.paymentIntentId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [failedPaymentsQuery.data?.rows, failedPaymentsSearch]);

  return (
    <div style={{ padding: "clamp(1.25rem,3vw,2rem)", flex: 1, overflowY: "auto" }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: "1.25rem", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: ".45rem", padding: ".28rem .6rem", borderRadius: 999, background: "rgba(233,30,140,.12)", border: "1px solid rgba(233,30,140,.28)", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase" }}>
            <CreditCard size={13} /> Stripe operations
          </div>
          <h1 style={{ margin: ".7rem 0 .25rem", color: "white", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.75rem", letterSpacing: "0.04em" }}>Payments</h1>
          <p style={{ maxWidth: 650, margin: 0, color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>Every membership subscription on the configured price, read live from Stripe, with the reference needed to reconcile each one.</p>
        </div>
        <div role="note" style={{ maxWidth: 355, padding: ".75rem .9rem", borderRadius: ".8rem", background: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.09)", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
          <strong style={{ color: "#E2E8F0" }}>Privacy-safe operational view.</strong> No card data, billing details, receipts, or charge data is stored or rendered here. Stripe remains the source of truth for payment details.
        </div>
      </header>

      {/* Filters sit above the table they act on. */}
      <CrmFilterTiles
        ariaLabel="Membership summary"
        tiles={(["all", "active", "past_due", "cancelled", "comped"] as MembershipFilter[]).map(key => {
          const meta = membershipFilterMeta[key];
          return {
            key,
            label: meta.label,
            value: membershipSummary[key],
            icon: meta.icon,
            color: meta.color,
            background: meta.background,
            border: meta.border,
            selected: membershipFilter === key,
            onSelect: () => setMembershipFilter(key),
          };
        })}
      />

      {membershipFilter !== "all" && (
        <section aria-label="Membership filters" style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
          <button type="button" onClick={() => setMembershipFilter("all")} style={{ minHeight: 40, padding: ".45rem .8rem", borderRadius: 999, border: "1px solid rgba(233,30,140,.6)", background: "rgba(233,30,140,.14)", color: "#FBCFE8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}>
            Clear {membershipFilterMeta[membershipFilter].label.toLowerCase()} filter
          </button>
        </section>
      )}

      {/*
        The same panel the members and speakers tables use: name, how to read it,
        then the search with the count beside it. This view had its own header
        arrangement and a search floating above the panel entirely, so three
        tables in one product read as three different tables.
      */}
      <CrmTablePanel
        title="Membership transactions"
          description={membershipQuery.data?.isExampleData
            ? "Example rows built from the local database — this screen does not contact Stripe outside production."
          : "Every subscription on the configured membership price. Active and Comped are independent assignments, so a fully discounted active membership appears in both operating views. Cancelled and past-due memberships remain separately visible for reconciliation."}
        icon={CreditCard}
        search={membershipSearch}
        onSearchChange={setMembershipSearch}
        searchLabel="Search memberships"
        searchPlaceholder="Search member, code or reference"
        shownCount={visibleMemberships.length}
        totalCount={membershipQuery.data?.rows.length ?? 0}
        noun="membership"
      >
        <div style={{ padding: "0 1.1rem 1.1rem" }}>
          <div style={{ marginBottom: ".85rem" }}>
            {/*
              Says which it is. In development these rows are built from the local
              database rather than read from Stripe, and a plausible-looking
              amount that is not real is exactly the sort of thing that ends up in
              a screenshot and then in a decision.
            */}
            {membershipQuery.data?.isExampleData ? (
              <p style={{ margin: "0 0 .25rem", display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".18rem .5rem", borderRadius: 999, background: "rgba(245,166,35,.14)", border: "1px solid rgba(245,166,35,.4)", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>
                Example data · not from Stripe
              </p>
            ) : (
              <p style={{ margin: "0 0 .25rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase" }}>Live from Stripe</p>
            )}
          </div>
          {/*
            The configured Stripe price ID is deliberately not shown here. It is
            operator configuration rather than transaction data, and this view gets
            opened in front of other people. Settings is where it lives, and the
            price panel there also reports the verified amount and whether the price
            is in test mode — which is the part worth reading.
          */}

        {membershipQuery.isLoading ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Reading transactions from Stripe…</p>
        ) : membershipQuery.data?.error ? (
          /*
           * Shown rather than thrown, so a Stripe outage reports itself instead of
           * rendering an empty table that reads as "no memberships".
           *
           * Stripe's own message is not printed. It quotes the price ID back, which
           * is the operator configuration this view deliberately does not display,
           * and it names a cause an operator cannot act on. "No such price" almost
           * always means the configured price and the API key are in different
           * modes — a live price cannot be read with a test key — so that is what
           * the message says.
           */
          <p role="alert" style={{ margin: 0, color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
            {/^no such price/i.test(membershipQuery.data.error)
              ? "Stripe has no membership price matching the configured one. A live price cannot be read with a test key, or the other way round — check that the price ID in Settings and the Stripe secret key are in the same mode."
              : "Stripe could not be read. Check the Stripe connection, then reload."}
          </p>
        ) : (membershipQuery.data?.rows.length ?? 0) === 0 ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>No membership subscriptions exist on this price yet.</p>
        ) : visibleMemberships.length === 0 ? (
          <p role="status" style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            No membership matches this filter. {membershipSummary.all} total.
          </p>
        ) : (
          /* Carries the shared table surface, so this reads as the same kind of
             object as the members, speakers, and events tables. */
          <div className="crm-table-scroll" style={{ overflowX: "auto", background: "var(--chi-table-surface)", border: "1px solid rgba(255,255,255,.08)", borderRadius: ".9rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
              <caption className="sr-only">Membership transactions from Stripe</caption>
              <thead>
                <tr>
                  {["Member", "Status", "Subscription date", "Collected", "Discount code", "Renews on", "Stripe reference"].map(heading => (
                    <th key={heading} scope="col" style={{ padding: ".5rem .6rem", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,.1)", color: "#7C899D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".08em", textTransform: "uppercase", whiteSpace: "nowrap" }}><CrmColumnHeading label={heading} icon={heading === "Member" ? User : undefined} /></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleMemberships.map(row => (
                  <tr key={row.subscriptionId}>
                    <td style={membershipCell}>
                      <span style={{ color: "#E2E8F5", fontWeight: 700 }}>{row.name ?? row.email ?? "Unnamed customer"}</span>
                      {row.email && row.name && <><br /><span style={{ color: "#8390A6", fontSize: "var(--chi-body-compact)" }}>{row.email}</span></>}
                    </td>
                    <td style={membershipCell}>
                      <span style={{ color: row.status === "active" ? "#86EFAC" : row.status === "past_due" ? "#FCD34D" : "#94A3B8", fontWeight: 700, textTransform: "capitalize" }}>{row.status.replace(/_/g, " ")}</span>
                      {row.cancelAtPeriodEnd && <><br /><span style={{ color: "#FCA5A5", fontSize: "var(--chi-body-compact)" }}>Cancels at period end</span></>}
                    </td>
                    <td style={membershipCell}><span style={{ color: "#AEB9C9" }}>{formatStripeDate(row.startedAt)}</span></td>
                    <td style={membershipCell}>
                      {row.amountPaidCents === null
                        ? <span style={{ color: "#728096" }}>Unknown</span>
                        : <span style={{ color: row.comped ? "#FCD34D" : "#E2E8F5", fontWeight: 700 }}>{formatMoney(row.amountPaidCents, row.currency)}</span>}
                      {/* A comped membership is otherwise indistinguishable from a
                          paid one at a glance, which is how these went unnoticed. */}
                      {row.comped && <><br /><span style={{ color: "#FCD34D", fontSize: "var(--chi-body-compact)" }}>Nothing collected</span></>}
                    </td>
                    <td style={membershipCell}>
                      {row.discountCode
                        ? <><code style={{ color: "#C4B5FD", fontSize: "var(--chi-body-compact)" }}>{row.discountCode}</code>{row.discountLabel && <><br /><span style={{ color: "#8390A6", fontSize: "var(--chi-body-compact)" }}>{row.discountLabel}</span></>}</>
                        : <span style={{ color: "#728096" }}>—</span>}
                    </td>
                    <td style={membershipCell}><span style={{ color: "#AEB9C9" }}>{formatStripeDate(row.renewsAt)}</span></td>
                    <td style={membershipCell}>
                      <code style={{ color: "#7DEAFF", fontSize: "var(--chi-body-compact)", wordBreak: "break-all" }}>{row.paymentIntentId ?? row.subscriptionId}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </CrmTablePanel>

      <div style={{ marginTop: "1.25rem" }}>
        <CrmTablePanel
          title="Failed payment attempts"
          description={failedPaymentsQuery.data?.isExampleData
            ? "Failed Stripe payment attempts are unavailable in local example data."
            : "Recent declined or canceled Stripe payment attempts. These are operational alerts only and are never counted as collected revenue or paid membership."}
          icon={AlertTriangle}
          search={failedPaymentsSearch}
          onSearchChange={setFailedPaymentsSearch}
          searchLabel="Search failed payments"
          searchPlaceholder="Search member, decline or reference"
          shownCount={visibleFailedPayments.length}
          totalCount={failedPaymentsQuery.data?.rows.length ?? 0}
          noun="failed payment"
        >
          <div style={{ padding: "0 1.1rem 1.1rem" }}>
            {failedPaymentsQuery.isLoading ? (
              <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Reading failed payment attempts from Stripe…</p>
            ) : failedPaymentsQuery.data?.error ? (
              <p role="alert" style={{ margin: 0, color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Stripe could not be read. Check the Stripe connection, then reload.</p>
            ) : (failedPaymentsQuery.data?.rows.length ?? 0) === 0 ? (
              <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>No recent failed payment attempts.</p>
            ) : (
              <div className="crm-table-scroll" style={{ overflowX: "auto", background: "var(--chi-table-surface)", border: "1px solid rgba(244,63,94,.19)", borderRadius: ".9rem" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
                  <caption className="sr-only">Recent failed Stripe payment attempts</caption>
                  <thead>
                    <tr>
                      {["Member", "Failure", "Amount", "Attempted", "Stripe reference"].map(heading => (
                        <th key={heading} scope="col" style={{ padding: ".5rem .6rem", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,.1)", color: "#7C899D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".08em", textTransform: "uppercase", whiteSpace: "nowrap" }}><CrmColumnHeading label={heading} icon={heading === "Member" ? User : undefined} /></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFailedPayments.map(row => (
                      <tr key={row.paymentIntentId}>
                        <td style={membershipCell}>
                          <span style={{ color: "#E2E8F5", fontWeight: 700 }}>{row.name ?? row.email ?? "Unnamed customer"}</span>
                          {row.email && row.name && <><br /><span style={{ color: "#8390A6", fontSize: "var(--chi-body-compact)" }}>{row.email}</span></>}
                        </td>
                        <td style={membershipCell}>
                          <span style={{ color: "#FDA4AF", fontWeight: 700 }}>{row.failureMessage ?? "Payment could not be completed"}</span>
                          {row.failureCode && <><br /><code style={{ color: "#FCA5A5", fontSize: "var(--chi-body-compact)" }}>{row.failureCode.replace(/_/g, " ")}</code></>}
                        </td>
                        <td style={membershipCell}><span style={{ color: "#E2E8F5", fontWeight: 700 }}>{row.amountCents === null ? "Unknown" : formatMoney(row.amountCents, row.currency)}</span></td>
                        <td style={membershipCell}><span style={{ color: "#AEB9C9" }}>{formatStripeDate(row.occurredAt)}</span></td>
                        <td style={membershipCell}><code style={{ color: "#FDA4AF", fontSize: "var(--chi-body-compact)", wordBreak: "break-all" }}>{row.paymentIntentId}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CrmTablePanel>
      </div>

    </div>
  );
}
